import { hasOutstandingRestorationWork, type RestorationWorkStatus } from '../lib/restorationWork';
import { useEffect, useRef, useState } from 'react';

/** Refresh persisted values while visible; never infer prices from discovery jobs.
 * Fast checks cover worker completion. Afterwards, pending evidence is re-read
 * at most twelve times per focused hour: the daily verifier can qualify it later.
 * Requests never overlap even when focus changes mid-request.
 */
export function usePendingValuationRefresh(
  pending: boolean,
  focused: boolean,
  refresh: (active: () => boolean) => Promise<void | boolean>,
  scope?: string,
  itemIds?: string[]
): RestorationWorkStatus {
  const refreshRef = useRef(refresh);
  const inFlight = useRef(false);
  refreshRef.current = refresh;
  const [workStatus, setWorkStatus] = useState<{ key: string; status: RestorationWorkStatus } | null>(null);
  const workIds = [...new Set(itemIds ?? [])].sort().join(',');
  const statusKey = JSON.stringify([scope, workIds]);

  useEffect(() => {
    if (!pending || !focused) return;
    let disposed = false;
    let workerCompleted = false;
    setWorkStatus(null);
    let timer: ReturnType<typeof setTimeout>;
    const started = Date.now();
    const schedule = () => {
      if (!disposed) timer = setTimeout(tick, workerCompleted ? 300000 : Date.now() - started < 120000 ? 5000 : 15000);
    };
    const tick = async () => {
      if (!inFlight.current) {
        inFlight.current = true;
        try {
          let outstanding: boolean | undefined;
          try {
            outstanding = workerCompleted ? false : !workIds || await hasOutstandingRestorationWork(workIds.split(','), status => {
              if (!disposed) setWorkStatus({ key: statusKey, status });
            });
          } catch {
            // Lifecycle is an advisory stop signal, never a gate on value reads.
            if (!disposed) setWorkStatus({ key: statusKey, status: {} });
            console.error('Valuation lifecycle unavailable; continuing authoritative refresh');
          }
          if (disposed) return;
          const refreshed = await refreshRef.current(() => !disposed);
          // Successful completion ends rapid/lifecycle polling, not evidence reads.
          // Keep completed unavailable wording while waiting for later qualification.
          if (outstanding === false && refreshed !== false && !disposed) workerCompleted = true;
        } catch {
          console.error('Pending valuation refresh failed');
        } finally {
          inFlight.current = false;
        }
      }
      schedule();
    };
    schedule();
    return () => {
      disposed = true;
      clearTimeout(timer);
    };
  }, [pending, focused, scope, workIds, statusKey]);
  return workStatus?.key === statusKey ? workStatus.status : {};
}
